import { z } from 'zod';

// FHIR R4 types shared by the Medplum-backed EHR vendors (Sugar, Orchard)
export const fhirPatientNameSchema = z.object({
  use: z.string().optional(),
  given: z.array(z.string()).optional(),
  family: z.string().optional(),
});

export const fhirPatientSchema = z.object({
  resourceType: z.literal('Patient'),
  id: z.string(),
  name: z.array(fhirPatientNameSchema).optional(),
  birthDate: z.string().optional(),
  gender: z.string().optional(),
});

export type FhirPatient = z.infer<typeof fhirPatientSchema>;

export const fhirEncounterSchema = z.object({
  resourceType: z.literal('Encounter'),
  id: z.string(),
  status: z.string(),
  period: z
    .object({
      start: z.string().optional(),
      end: z.string().optional(),
    })
    .optional(),
  type: z
    .array(
      z.object({
        text: z.string().optional(),
        coding: z
          .array(
            z.object({
              code: z.string().optional(),
              display: z.string().optional(),
            }),
          )
          .optional(),
      }),
    )
    .optional(),
  subject: z
    .object({
      reference: z.string().optional(),
    })
    .optional(),
  participant: z
    .array(
      z.object({
        individual: z
          .object({
            reference: z.string().optional(),
            display: z.string().optional(),
          })
          .optional(),
      }),
    )
    .optional(),
});

export type FhirEncounter = z.infer<typeof fhirEncounterSchema>;

export const fhirBundleSchema = <T extends z.ZodTypeAny>(resourceSchema: T) =>
  z.object({
    resourceType: z.literal('Bundle'),
    type: z.string(),
    entry: z
      .array(
        z.object({
          resource: resourceSchema,
        }),
      )
      .optional(),
  });

export type FhirBundle<T> = {
  resourceType: 'Bundle';
  type: string;
  entry?: Array<{
    resource: T;
  }>;
};

export const appointmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  patient: z.object({
    id: z.string(),
    fullName: z.string(),
  }),
});

export type Appointment = z.infer<typeof appointmentSchema>;

export const fhirCompositionSectionSchema = z.object({
  title: z.string().optional(),
  code: z
    .object({
      coding: z
        .array(
          z.object({
            system: z.string().optional(),
            code: z.string().optional(),
            display: z.string().optional(),
          }),
        )
        .optional(),
    })
    .optional(),
  text: z
    .object({
      status: z.string().optional(),
      div: z.string().optional(),
    })
    .optional(),
});

export type FhirCompositionSection = z.infer<
  typeof fhirCompositionSectionSchema
>;

export const fhirCompositionSchema = z
  .object({
    resourceType: z.literal('Composition'),
    id: z.string(),
    status: z.string().optional(),
    date: z.string().optional(),
    title: z.string().optional(),
    subject: z
      .object({
        reference: z.string().optional(),
      })
      .optional(),
    encounter: z
      .object({
        reference: z.string().optional(),
      })
      .optional(),
    section: z.array(fhirCompositionSectionSchema).optional(),
  })
  .passthrough();

export type FhirComposition = z.infer<typeof fhirCompositionSchema>;

export const fhirCompositionBundleSchema = fhirBundleSchema(
  fhirCompositionSchema,
);

export const SOAP_LOINC_CODES: Record<string, string> = {
  '61150-9': 'subjective',
  '61149-1': 'objective',
  '51848-0': 'assessment',
  '51847-2': 'plan',
};

export const getSoapKeyFromSection = (
  section: FhirCompositionSection,
): string | undefined => {
  const codings = section.code?.coding ?? [];
  for (const coding of codings) {
    if (coding.code && SOAP_LOINC_CODES[coding.code]) {
      return SOAP_LOINC_CODES[coding.code];
    }
  }
  return undefined;
};

export const stripFhirHtml = (html: string): string => {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .trim();
};
